import request from '@host-panel/request';

export type TConfigDB = 'tenant';
export type TConfigType = number | string | object | boolean | any[];

export type TConfigRow = {
  db: TConfigDB;
  namespace: string;
  key: string;
  value: TConfigType;
  expose: boolean;
  active: boolean;
  updated_at: Date | string;
};

export type TConfigPayload = {
  configuration: TConfigRow[];
  spotName: string;
};

export type TConfigKey = Partial<TConfigRow>;

function keySplitter(keyStr: string): { db: TConfigDB; namespace: string; key: string } {
  const splittedKey: (string | TConfigDB)[] = keyStr.split('.');

  if (splittedKey.length !== 3) {
    throw new Error(`Invalid key length, got: ${keyStr}. Expected: db.namespace.key`);
  }

  const [db, namespace, key] = splittedKey;

  if (db !== 'tenant') {
    throw new Error(`Invalid db name, got: ${db}. Expected: tenant`);
  }

  return { db, namespace, key };
}

export function setSpotConfig(
  spotId: string,
  keys: TConfigKey[] | { [configKey in string]: TConfigType } = [],
  spotName: string = spotId
): Promise<any> {
  const defaultOptions = {
    active: true,
    expose: true,
    updated_at: new Date()
  };

  let configuration = [];

  if (Array.isArray(keys)) {
    configuration = keys.map(
      ({ db, namespace, key, value, ...options }: TConfigKey): TConfigRow => {
        let separatedKey = { db, namespace, key };
        if (!db || !namespace) {
          separatedKey = keySplitter(key);
        }
        return {
          ...separatedKey,
          value,
          ...{ ...defaultOptions, ...options }
        };
      }
    );
  } else if (Object.keys(keys).length) {
    configuration = Object.keys(keys).reduce(
      (acc, curr) => [...acc, { ...defaultOptions, ...keySplitter(curr), value: keys[curr] }],
      []
    );
  }

  return request(`/host-panel/config/${spotId}`, {
    method: 'PATCH',
    data: {
      configuration,
      spotName: spotName || spotId
    }
  });
}

/**
 *
 * @param spotId
 * @param {string[]} keys - The required fields from the tenant configuration
 * @returns {Promise<any>} Returns Promise which resolves to the tenant configuration object filtered by the given keys
 * @example getConfig('sp_123', ['tenant.pitc.enabled']) returns { tenant: { pitc: { enabled: true } } }
 */

export function getSpotConfig(
  spotId: string,
  keys: string[],
  { byNameSpace } = { byNameSpace: false }
): Promise<any> {
  return request(`/host-panel/${byNameSpace ? 'namespace-config' : 'config'}/${spotId}`, {
    method: 'POST',
    data: {
      fields: keys
    }
  });
}

export function getSpotModeratorsConfig(spotId: string, keys: string[]): Promise<any> {
  return request(`/host-panel/moderation-config/${spotId}`, {
    method: 'POST',
    data: {
      fields: keys
    }
  });
}

/**
 *
 * @param spotId
 * @returns {Promise<any>} Returns Promise which resolves to the init configuration object
 **/
export function getV1Config(spotId: string, keys: string[]): Promise<any> {
  return request(`/host-panel/v1-config/${spotId}`, {
    method: 'POST',
    data: { fields: keys }
  });
}

/**
 * @example setConfig('sp_123', [{ key: 'lazy_load_margin', value: true}])
 */
export function setV1Config(spotId: string, keys: TConfigKey[] = []): Promise<any> {
  return request(`/host-panel/v1-config/${spotId}`, {
    method: 'PATCH',
    data: {
      configuration: keys
    }
  });
}
